import { Card, CardContent } from "../../components/ui/card";
import {
  MapPin,
  Calendar,
  Clock,
  Phone,
  Navigation,
  Mail,
  User,
  Building,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { FormattedMessage, useIntl } from "react-intl";

function ClientInfoCard({ appointment, status, onStatusChange }) {
  const intl = useIntl();
  if (!appointment) return null; // allow empty clientDetails
  const client = appointment.clientDetails || {};
  const APPOINTMENT_STATUSES = [
    {
      id: 21,
      label: intl.formatMessage({ id: "Appointment.Status.OnMyWay" }),
      color: "bg-blue-500",
    },
    {
      id: 22,
      label: intl.formatMessage({ id: "Appointment.Status.NotStarted" }),
      color: "bg-gray-400",
    },
    {
      id: 23,
      label: intl.formatMessage({ id: "Appointment.Status.Start" }),
      color: "bg-green-500",
    },
    {
      id: 28,
      label: intl.formatMessage({ id: "Appointment.Status.Paused" }),
      color: "bg-yellow-500",
    },
    {
      id: 24,
      label: intl.formatMessage({ id: "Appointment.Status.Completed" }),
      color: "bg-emerald-600",
    },
  ];

  const STATUS_TRANSITIONS = {
    22: [21, 23],
    21: [23, 28, 24],
    23: [28, 24],
    28: [23, 24],
    24: [],
  };
  // Build full shipping address
  const shipping = client.shippingAddress;
  const appointmentAddress = shipping
    ? [
        shipping.address1,
        shipping.address2,
        shipping.city,
        shipping.province,
        shipping.country,
        shipping.zip,
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  const allowedStatuses = STATUS_TRANSITIONS[status] || [];
  const statusObj = APPOINTMENT_STATUSES.find((s) => s.id === status) || {
    label: "Not Started",
    color: "bg-gray-400",
  };

  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString() : "N/A";

  const cleanPhone = (phone) => (phone ? phone.replace(/Cell\s+/, "") : "");

  const handleCall = () => {
    const phone =
      client.phone || appointment.technicianDetails?.phone || appointment.phone;
    if (phone) window.open(`tel:${phone}`, "_self");
  };

  const handleOpenMaps = () => {
    if (!appointmentAddress) return;
    const encoded = encodeURIComponent(appointmentAddress);
    window.open(`https://www.google.com/maps?q=${encoded}`, "_blank");
  };

  const formatTime = (timeString) => {
    if (!timeString || typeof timeString !== "string") return "";
    const today = new Date().toISOString().split("T")[0];
    const fullDateTimeString = `${today}T${timeString}`;
    const date = new Date(fullDateTimeString);
    if (isNaN(date)) {
      return "Invalid Time";
    }
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Card className="mobile-card">
      <CardContent className="!p-4">
        <div className="flex flex-col space-y-3">
          {/* Header Row - Appointment Number and Status */}
          <div className="flex items-center justify-between">
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {appointment.appointmentNumber || "N/A"}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-3 !py-2 border border-gray-200 flex items-center gap-2"
                >
                  <div className={`${statusObj.color} w-3 h-3 rounded-full`} />
                  <span className="text-xs font-semibold">
                    {statusObj.label}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {allowedStatuses.length === 0 ? (
                  <DropdownMenuItem disabled>
                    <FormattedMessage id="Appointment.NoStatusChange" />
                  </DropdownMenuItem>
                ) : (
                  APPOINTMENT_STATUSES.filter((s) =>
                    allowedStatuses.includes(s.id)
                  ).map(({ id, label, color }) => (
                    <DropdownMenuItem
                      key={id}
                      onClick={() => onStatusChange(id)}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`${color} w-3 h-3 rounded-full`} />
                        <span className="text-xs">{label}</span>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Customer Info */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <div className="flex-1">
              <span className="text-md font-semibold text-gray-800 dark:text-gray-100">
                {client.name || client.companyName || "N/A"}
              </span>
              <div className="flex gap-1 mt-1">
                {client.isResidential && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                  >
                    Residential
                  </Badge>
                )}
                {client.isCommercial && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                  >
                    Commercial
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {client.companyName && (
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <Building className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span>{client.companyName}</span>
            </div>
          )}

          {/* Phone & Call */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 flex-1">
              <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span>{cleanPhone(client.phone) || "N/A"}</span>
            </div>
          </div>

          {/* Email */}
          {client.email && (
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <Mail className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span>{client.email}</span>
            </div>
          )}

          {/* Address & Maps */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-2">
            <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 flex-1">
              <MapPin className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5" />
              <p className="leading-tight">
                {appointmentAddress || (
                  <FormattedMessage id="Appointment.AddressUnavailable" />
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2 w-full">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCall}
              disabled={!client.phone}
              className="flex-1 flex items-center gap-1 px-3 py-1.5 justify-center border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
            >
              <Phone className="h-3 w-3 text-blue-600 dark:text-blue-400" />
              <FormattedMessage id="Appointment.Call" />
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenMaps}
              disabled={!appointmentAddress}
              className="flex-1 flex items-center gap-1 px-3 py-1.5 justify-center border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
            >
              <Navigation className="h-3 w-3 text-green-600 dark:text-green-400" />
              <FormattedMessage id="Appointment.Maps" />
            </Button>
          </div>

          {/* Date & Time */}
          <div className="flex flex-row gap-6 bg-gray-50 dark:bg-muted rounded-lg p-3">
            {appointment.startDate && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="font-semibold text-gray-800 dark:text-gray-100">
                  {formatDate(appointment.startDate)}
                </span>
              </div>
            )}
            {appointment.startTime && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500 dark:text-orange-400" />
                <span className="font-semibold text-gray-800 dark:text-gray-100">
                  {formatTime(appointment.startTime)}
                </span>
              </div>
            )}
          </div>

          {/* Billing Contact */}
          {client.billingCPName && client.billingCPName !== client.name && (
            <div className="border-t pt-3">
              <p className="text-xs text-gray-500 mb-2">
                <FormattedMessage id="Appointment.BillingContact" />
              </p>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{client.billingCPName}</span>
                {client.billingCPPhone && (
                  <span className="text-gray-600">
                    {cleanPhone(client.billingCPPhone)}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ClientInfoCard;
